import { flowRight } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from '../../../../common/components/runtime-context';
import classNames from 'classnames';
import { COVER_TYPE_IMAGE } from '../../../constants/cover-types';
import ScrollableContainer from '../../scrollable-container';
import { HorizontalSeparator } from '../../separator';
import ArrowBackIcon from '../../icons/arrow-back-icon';
import ImageInput from './cover-image-input';
import Header from '../../header';
import HeaderButton from '../../header-button';
import { getHeaderTitle } from '../../../services/categories-service';
import { getIsHeaderEnabled } from '../../../selectors/app-settings-selectors';
import { getIsUploadLoading } from '../../../selectors/is-loading-selectors';
import CategorySettingsTextInputWithLabel from './category-settings-text-input-with-label';
import CategorySettingsRadioInputWithLabel from './category-settings-radio-input-with-label';
import CategoriesManagerFooterButtons from './categories-manager-footer-buttons';
import withTranslate from '../../../hoc/with-translate';
import withFontClassName from '../../../hoc/with-font-class-name';
import withDeviceType from '../../../hoc/with-device-type';
import withSettingsColor from '../../../hoc/with-settings-color';
import { wrapUploadImageAction } from '../../../actions/image-upload/upload-wrapper';
import styles from './category-settings-form.scss';
import { getIsMobile } from '../../../../common/store/basic-params/basic-params-selectors';

const INPUT_ID_CATEGORY_TITLE = 'INPUT_ID_CATEGORY_TITLE';
const INPUT_ID_HEADER_TITLE = 'INPUT_ID_HEADER_TITLE';
const INPUT_ID_DESCRIPTION = 'INPUT_ID_DESCRIPTION';
const INPUT_ID_READ_PERMISSIONS = 'INPUT_ID_READ_PERMISSIONS';
const INPUT_ID_WRITE_PERMISSIONS = 'INPUT_ID_WRITE_PERMISSIONS';
const INPUT_LIMIT_CATEGORY_TITLE = 25;
const INPUT_LIMIT_HEADER_TITLE = 25;
const INPUT_LIMIT_CATEGORY_DESCRIPTION = 140;

const UPLOAD_ID = 'categories-manager-setting-form';

class CategorySettingsForm extends Component {
  state = {
    isUploadingImage: false,
    isSaving: false,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isUploading === true && this.props.isUploading === false) {
      this.setState({ isUploadingImage: false });
    }
  }

  forDesktop = a => {
    return this.props.isMobile ? null : a;
  };

  cancelChanges = () => {
    this.props.cancelChanges();
  };

  saveChanges = () => {
    const { isUploadingImage, isSaving } = this.state;
    if (!isUploadingImage && !isSaving) {
      this.setState({ isSaving: true });
      this.props.saveChanges();
    }
  };

  updateCoverImage = ({ coverType, file }) => {
    if (coverType === COVER_TYPE_IMAGE) {
      this.props
        .uploadImage(file, { coverType, categoryId: this.props.categoryId, uploadId: UPLOAD_ID })
        .then(({ dataUrl }) => {
          this.setState({ isUploadingImage: true });
          this.props.updateChangesLocally({ cover: dataUrl, coverType });
        });
    } else {
      this.props.updateChangesLocally({ coverType });
    }
  };

  updateCategoryTitle = value => {
    this.props.updateChangesLocally({ label: value });
  };

  updateHeaderTitle = value => {
    this.props.updateChangesLocally({ headerTitle: value });
  };

  updateDescription = value => {
    this.props.updateChangesLocally({ description: value });
  };

  updateReadPermissions = value => {
    this.props.updateChangesLocally({ isPrivate: value });
  };

  updateWritePermissions = value => {
    this.props.updateChangesLocally({ isWriteProtected: value });
  };

  render() {
    const {
      t,
      category,
      isNewCategory,
      titleFontClassName,
      contentFontClassName,
      isMobile,
      isHeaderEnabled,
      translationsPrefix,
      showPermissionsSettings,
      isReadPermissionsDisabled,
      textColor,
    } = this.props;

    const p = txt => `${translationsPrefix}.${txt}`;

    const cancelBtnText = p('button-cancel');
    const saveBtnText = isNewCategory ? p('button-create') : p('button-update');

    return (
      <div
        role="form"
        style={{ color: textColor }}
        className={classNames(styles.container, contentFontClassName)}
        data-hook="category-settings-form"
      >
        {isMobile && (
          <Header
            left={
              <HeaderButton
                onClick={this.cancelChanges}
                text={cancelBtnText}
                dataHook="leftButton"
              />
            }
            right={
              <HeaderButton onClick={this.saveChanges} text={saveBtnText} dataHook="rightButton" />
            }
          />
        )}
        <ScrollableContainer>
          <div className={styles.content}>
            {!isMobile && (
              <div className={styles.header}>
                <ArrowBackIcon
                  className={classNames(styles.iconBack, 'forum-icon-fill', 'button-hover-fill')}
                  onClick={this.cancelChanges}
                />
                <h3 className={classNames(styles.title, titleFontClassName)}>
                  {t(isNewCategory ? p('title-create') : p('title-edit'))}
                </h3>
              </div>
            )}
            {!isMobile && <HorizontalSeparator className={styles.separator} />}
            <CategorySettingsTextInputWithLabel
              dataHook="category-settings-form__title"
              id={INPUT_ID_CATEGORY_TITLE}
              labelText={t(p('title-label'))}
              tooltipText={this.forDesktop(t(p('title-tooltip')))}
              placeholder={t(p('title-placeholder'))}
              limit={INPUT_LIMIT_CATEGORY_TITLE}
              value={category.label}
              onChange={this.updateCategoryTitle}
              wrapperClassName={styles.inputWrapper}
              forceLastValue
            />
            {isHeaderEnabled && (
              <CategorySettingsTextInputWithLabel
                id={INPUT_ID_HEADER_TITLE}
                labelText={t(p('header-title-label'))}
                tooltipText={this.forDesktop(t(p('header-title-tooltip')))}
                placeholder={t(p('header-title-placeholder'))}
                limit={INPUT_LIMIT_HEADER_TITLE}
                value={getHeaderTitle(category)}
                onChange={this.updateHeaderTitle}
                wrapperClassName={styles.inputWrapper}
              />
            )}
            <CategorySettingsTextInputWithLabel
              id={INPUT_ID_DESCRIPTION}
              labelText={t(p('description-label'))}
              tooltipText={this.forDesktop(t(p('description-tooltip')))}
              placeholder={t(p('description-placeholder'))}
              limit={INPUT_LIMIT_CATEGORY_DESCRIPTION}
              value={category.description}
              onChange={this.updateDescription}
              wrapperClassName={styles.inputWrapper}
              minRows={4}
              isTextArea
            />
            <ImageInput
              fontClass={contentFontClassName}
              imgSrc={category.cover}
              coverType={category.coverType}
              onChange={this.updateCoverImage}
              wrapperClassName={styles.inputWrapper}
              isLoading={this.state.isUploadingImage}
              deleteTooltipText={t(p('delete-cover'))}
              uploadText={t(p('upload-cover'))}
              changeText={t(p('change-cover'))}
            />
            {showPermissionsSettings && (
              <CategorySettingsRadioInputWithLabel
                id={INPUT_ID_READ_PERMISSIONS}
                labelText={t(p('read-permissions-label'))}
                tooltipText={this.forDesktop(t(p('read-permissions-tooltip')))}
                name="isPrivate"
                value={category.isPrivate || false}
                options={[
                  { value: false, text: t(p('read-permissions-all')) },
                  { value: true, text: t(p('read-permissions-members')) },
                ]}
                onChange={this.updateReadPermissions}
                isDisabled={isReadPermissionsDisabled}
              />
            )}
            {showPermissionsSettings && <HorizontalSeparator className={styles.separator} />}
            {showPermissionsSettings && (
              <CategorySettingsRadioInputWithLabel
                id={INPUT_ID_WRITE_PERMISSIONS}
                labelText={t(p('write-permissions-label'))}
                tooltipText={this.forDesktop(t(p('write-permissions-tooltip')))}
                name="isWriteProtected"
                value={category.isWriteProtected || false}
                options={[
                  { value: false, text: t(p('write-permissions-all')) },
                  { value: true, text: t(p('write-permissions-admins')) },
                ]}
                onChange={this.updateWritePermissions}
              />
            )}
          </div>
        </ScrollableContainer>
        {!isMobile && (
          <CategoriesManagerFooterButtons
            onClickLeft={this.cancelChanges}
            onClickRight={this.saveChanges}
            leftText={t(cancelBtnText)}
            rightText={t(saveBtnText)}
            isRightMuted={this.state.isUploadingImage}
            rightTooltipText={
              this.state.isUploadingImage ? t('categories-settings-form.img-is-uploading') : null
            }
            isRightLoading={this.state.isSaving}
          />
        )}
      </div>
    );
  }
}

CategorySettingsForm.propTypes = {
  categoryId: PropTypes.string,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  translationsPrefix: PropTypes.string,
  textColor: PropTypes.string,
  category: PropTypes.object,
  isMobile: PropTypes.bool,
  showPermissionsSettings: PropTypes.bool,
  isNewCategory: PropTypes.bool,
  isHeaderEnabled: PropTypes.bool,
  isReadPermissionsDisabled: PropTypes.bool,
  saveChanges: PropTypes.func,
  cancelChanges: PropTypes.func,
  updateChangesLocally: PropTypes.func,
  uploadImage: PropTypes.func,
  isUploading: PropTypes.bool,
  t: PropTypes.func,
};

const mapRuntimeToProps = (state, ownProps, actions) => ({
  isHeaderEnabled: getIsHeaderEnabled(state),
  uploadImage: wrapUploadImageAction(actions.uploadImageCategoriesManager, getIsMobile(state)),
  isUploading: getIsUploadLoading(state, UPLOAD_ID),
});

export default flowRight(
  withDeviceType,
  withTranslate,
  withFontClassName,
  withSettingsColor({
    propName: 'textColor',
    siteColorFallback: 'color-5',
    fallbackColor: '#000000',
  }),
  connect(mapRuntimeToProps),
)(CategorySettingsForm);
